import { useNavigate } from 'react-router-dom';
import { CalendarClock, Footprints, Ticket, Users, UtensilsCrossed } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { StatusPill } from '@/components/ui/status-pill';
import type { DiningBookingMode, PublicOffering } from '@/features/dining/types';
import { MODE_LABEL } from '@/features/dining/types';
import { ImageCarousel } from '@/components/image-carousel';

const IST = 'Asia/Kolkata';

const SECTION: Record<DiningBookingMode, { title: string; icon: LucideIcon; cta: string }> = {
  TABLE_RESERVATION: { title: 'Reserve a table', icon: UtensilsCrossed, cta: 'Reserve' },
  ENTRY_PASS: { title: 'Entry passes', icon: Ticket, cta: 'Buy pass' },
  EVENT: { title: 'Upcoming events', icon: CalendarClock, cta: 'Get tickets' },
};

function price_label(o: PublicOffering): string {
  return `₹${o.price_inr}${o.price_unit === 'PER_GUEST' ? ' / guest' : ''}`;
}

function event_when(o: PublicOffering): string | null {
  if (!o.event_start) return null;
  return new Date(o.event_start).toLocaleString('en-IN', { timeZone: IST, dateStyle: 'medium', timeStyle: 'short' });
}

export function DiningBookingSection({
  offerings,
  walk_in,
  vendor_name,
  vendor_id,
}: {
  offerings: PublicOffering[];
  walk_in: boolean;
  vendor_name: string | null;
  vendor_id: string;
}) {
  const navigate = useNavigate();
  if (offerings.length === 0 && !walk_in) return null;

  const modes: DiningBookingMode[] = ['TABLE_RESERVATION', 'ENTRY_PASS', 'EVENT'];
  const by_mode = modes
    .map((m) => ({ mode: m, items: offerings.filter((o) => o.mode === m) }))
    .filter((g) => g.items.length > 0);

  return (
    <Card data-testid="dining-booking">
      <CardHeader>
        <CardTitle>Book your visit</CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col gap-5">
        {walk_in && (
          <div
            className="flex items-center gap-2 rounded-input border-2 border-success/30 bg-success/10 px-4 py-3 text-sm font-medium text-success"
            data-testid="walk-in-note"
          >
            <Footprints className="h-4 w-4 shrink-0" /> Walk-ins welcome — drop by during opening hours.
          </div>
        )}

        {by_mode.map(({ mode, items }) => {
          const { title, icon: Icon, cta } = SECTION[mode];
          return (
            <div key={mode} className="flex flex-col gap-2" data-testid={`dining-group-${mode}`}>
              <h3 className="flex items-center gap-1.5 text-sm font-bold text-muted-foreground">
                <Icon className="h-4 w-4" /> {title}
              </h3>
              <ul className="flex flex-col gap-2">
                {items.map((o) => {
                  const when = event_when(o);
                  const sold_out = o.remaining !== null && o.remaining <= 0;
                  return (
                    <li
                      key={o.id}
                      className="flex items-center gap-3 rounded-input border-2 border-brand-outline-variant/15 p-3"
                      data-testid={`offering-${o.id}`}
                    >
                      {o.images.length > 0 && (
                        <ImageCarousel images={o.images} size="h-16 w-16" className="shrink-0 self-start" />
                      )}
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="font-semibold">{o.name}</span>
                          {sold_out ? (
                            <StatusPill tone="warning">Sold out</StatusPill>
                          ) : o.remaining !== null ? (
                            <StatusPill tone="info">{o.remaining} left</StatusPill>
                          ) : null}
                        </div>
                        <div className="mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs text-muted-foreground">
                          <span className="flex items-center gap-1">
                            <Users className="h-3 w-3" /> {o.min_party}–{o.max_party}
                          </span>
                          {when && <span>{when}</span>}
                          {o.description && <span className="truncate">{o.description}</span>}
                        </div>
                        {/* Client change (9 Oct 2026): the booking amount comes off the bill. */}
                        {o.redeemable_against_bill && (
                          <p className="mt-1 text-xs font-semibold text-success" data-testid={`offering-redeemable-${o.id}`}>
                            This amount will be deducted from your bill at the venue
                          </p>
                        )}
                      </div>
                      <div className="shrink-0 text-right font-heading text-sm font-bold tabular-nums">{price_label(o)}</div>
                      <Button
                        size="sm"
                        variant="gradient"
                        disabled={sold_out}
                        onClick={() => navigate(`/app/book/${o.service_id}`, { state: { offering: o, vendor_name, vendor_id } })}
                        data-testid={`book-offering-${o.id}`}
                      >
                        {MODE_LABEL[mode] && cta}
                      </Button>
                    </li>
                  );
                })}
              </ul>
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
}
